// src/products/dataroom/hooks/usePermissionManagement.js
// Custom hook for managing Dataroom permissions

import { useState, useEffect, useCallback } from 'react';
import permissionService from '../services/permissionService';

export const usePermissionManagement = (projectId) => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load project users
  const loadUsers = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      const result = await permissionService.getProjectUsers(projectId);
      if (result.error) {
        throw result.error;
      }

      setUsers(result.data || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Load project groups
  const loadGroups = useCallback(async () => {
    if (!projectId) return;

    try {
      const result = await permissionService.getProjectTeams(projectId);
      if (result.error) {
        throw result.error;
      }

      setGroups(result.data || []);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError(err.message);
    }
  }, [projectId]);

  // Get current user permissions
  const loadUserPermissions = useCallback(async (userId) => {
    if (!projectId || !userId) return;

    try {
      const result = await permissionService.getUserPermissions(userId, projectId);
      if (result.error) {
        throw result.error;
      }

      setUserPermissions(result.data);
    } catch (err) {
      console.error('Error loading user permissions:', err);
      setError(err.message);
    }
  }, [projectId]);

  // Add user to project
  const addUser = useCallback(async (userId, role, permissions = {}) => {
    try {
      setError(null);

      const result = await permissionService.addUserToProject(projectId, userId, role, permissions);
      if (result.error) {
        throw result.error;
      }

      // Refresh users list
      await loadUsers();

      return result.data;
    } catch (err) {
      console.error('Error adding user:', err);
      setError(err.message);
      throw err;
    }
  }, [projectId, loadUsers]);

  // Update user permissions
  const updateUserPermissions = useCallback(async (userId, role, permissions) => {
    try {
      setError(null);

      const result = await permissionService.updateUserPermissions(projectId, userId, role, permissions);
      if (result.error) {
        throw result.error;
      }

      // Refresh users list
      await loadUsers();

      return result.data;
    } catch (err) {
      console.error('Error updating user permissions:', err);
      setError(err.message);
      throw err;
    }
  }, [projectId, loadUsers]);

  // Remove user from project
  const removeUser = useCallback(async (userId) => {
    try {
      setError(null);

      const result = await permissionService.removeUserFromProject(projectId, userId);
      if (result.error) {
        throw result.error;
      }

      // Refresh users list
      await loadUsers();

      return result.data;
    } catch (err) {
      console.error('Error removing user:', err);
      setError(err.message);
      throw err;
    }
  }, [projectId, loadUsers]);

  // Check item permission
  const checkPermission = useCallback(async (userId, itemId, permissionType) => {
    try {
      const result = await permissionService.checkItemPermission(userId, itemId, permissionType);
      if (result.error) {
        throw result.error;
      }

      return result.data;
    } catch (err) {
      console.error('Error checking permission:', err);
      return false;
    }
  }, []);

  // Get item permissions
  const getItemPermissions = useCallback(async (itemId) => {
    try {
      const result = await permissionService.getItemPermissions(itemId);
      if (result.error) {
        throw result.error;
      }

      return result.data;
    } catch (err) {
      console.error('Error fetching item permissions:', err);
      throw err;
    }
  }, []);

  // Update item permissions
  const updateItemPermissions = useCallback(async (itemId, permissions) => {
    try {
      setError(null);

      const result = await permissionService.updateItemPermissions(itemId, permissions);
      if (result.error) {
        throw result.error;
      }

      return result.data;
    } catch (err) {
      console.error('Error updating item permissions:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Refresh all data
  const refresh = useCallback(() => {
    loadUsers();
    loadGroups();
  }, [loadUsers, loadGroups]);

  // Load data on mount and when projectId changes
  useEffect(() => {
    if (projectId && !loading) {
      loadUsers();
      loadGroups();
    }
  }, [projectId, loadGroups, loadUsers, loading]); // ✅ Removed function dependencies to prevent loops, added loading check

  return {
    // State
    users,
    groups,
    userPermissions,
    loading,
    error,

    // Actions
    loadUsers,
    loadGroups,
    loadUserPermissions,
    addUser,
    updateUserPermissions,
    removeUser,
    checkPermission,
    getItemPermissions,
    updateItemPermissions,
    refresh,

    // Computed values
    hasUsers: users.length > 0,
    hasGroups: groups.length > 0,
    adminUsers: users.filter(u => u.role === 'admin'),
    memberUsers: users.filter(u => u.role === 'member'),
    viewerUsers: users.filter(u => u.role === 'viewer'),
    ownerUsers: users.filter(u => u.role === 'owner')
  };
};

export default usePermissionManagement;
